import React, { useContext, useEffect, useState } from "react";
import { Box } from "@mui/material";
import AuthSidePanel from "../common/AuthSidePanel/AuthSidePanel";
import AuthRightSidePanel from "../common/AuthSidePanel/AuthRightSidePanel";
import SmallScreenFooter from "../common/SmallScreenFooter";
import { AUTH_STATUS } from "../../context/enums/authEnums";
import OtpVerification from "../common/OtpVerification/OtpVerification";
import AuthContext from "../../context/authContext";
import { AlertBannerContext } from "@kivunova/kivufrontendcommon";
import LoginForm from "./LoginForm";
import LoginChangePassword from "./LoginChangePassword";
import { useVerifyEmailOtp } from "../../hooks/api/useVerifyOTP";
import {
  API_RESPONSE_CODE,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY
} from "@kivunova/kivufrontendcommon";
import useAuthorize, {
  SUPER_ADMIN_NOT_ALLOWED_ERROR
} from "../../hooks/api/useAuthorize";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_PORTAL_DASHBOARD,
  PORTAL_PREFIX
} from "@kivunova/kivufrontendcommon";
import { useLocation, useNavigate } from "react-router-dom";
import { MembershipStatus } from "../../types/membership";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

const Login: React.FC = () => {
  const location = useLocation();
  const [loginInfo, setLoginInfo] = useState<string | undefined>();
  const { authContextInfo, updateAuthContextInfo } = useContext(AuthContext);
  const { setAlert } = useContext(AlertBannerContext);
  const verifyEmailOtpMutation = useVerifyEmailOtp();
  const useAuthorizeMutation = useAuthorize();
  const { language, defaultLang } = useContext(KivuI18nContext);
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const redirectPath = queryParams.get("redirectPath");

  const backToLogin = () => {
    updateAuthContextInfo({
      ...authContextInfo,
      auth: { status: AUTH_STATUS.UNAUTHENTICATED }
    });
  };

  const redirectFromLoginSuccess = () => {
    const safeRedirectPath =
      redirectPath && !redirectPath.includes("/login") ? redirectPath : null;
    const redirectUrl =
      safeRedirectPath ||
      localizedPath(
        `${PORTAL_PREFIX}/${PAGE_PORTAL_DASHBOARD}`,
        language,
        defaultLang
      );
    navigate(redirectUrl, { replace: true });
  };

  const handleSubmitOtp = async (otp: string) => {
    if (!loginInfo) return;

    try {
      await verifyEmailOtpMutation.mutateAsync(
        { email: loginInfo, otp },
        {
          onSuccess: (response) => {
            const { code } = response.verifyEmailOtp;

            if (code === API_RESPONSE_CODE.OK) {
              if (
                authContextInfo.auth.status ===
                AUTH_STATUS.EMAIL_VERIFICATION_AND_PASSWORD_CHANGE_REQUIRED
              ) {
                updateAuthContextInfo({
                  ...authContextInfo,
                  auth: { status: AUTH_STATUS.CHANGE_PASSWORD_REQUIRED }
                });
              } else {
                backToLogin();
              }
            } else {
              // ⚠️ Expected API/business failure — OTP rejected
              sendAppLog(
                LOG_LEVEL.WARNING,
                "OTP verification rejected by API",
                buildLogContext(
                  "auth",
                  "verify-email-otp",
                  // userId/companyId/storeId not available — user is not yet fully authenticated
                  undefined,
                  undefined,
                  undefined,
                  { apiCode: String(code) }
                )
              );
              setAlert({
                severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
                code: code as ALERT_BANNER_CODE
              });
            }
          },
          onError: (error: Error) => {
            // ❌ Unexpected runtime/network error from verifyEmailOtp mutation
            captureAppException(
              "OTP verification failed with unexpected error",
              error,
              buildLogContext(
                "auth",
                "verify-email-otp",
                // userId/companyId/storeId not available — user is not yet fully authenticated
                undefined,
                undefined,
                undefined
              )
            );
            setAlert({
              severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
              code: error?.message
                ? (error.message as ALERT_BANNER_CODE)
                : ALERT_BANNER_CODE.INTERNAL_ERROR
            });
          }
        }
      );
      /* eslint-disable @typescript-eslint/no-explicit-any */
    } catch (error: any) {
      // ❌ Outer catch — handles any error that escapes the mutateAsync callbacks
      captureAppException(
        "OTP verification failed with unexpected error",
        error,
        buildLogContext(
          "auth",
          "verify-email-otp",
          // userId/companyId/storeId not available — user is not yet fully authenticated
          undefined,
          undefined,
          undefined
        )
      );
      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  };

  useEffect(() => {
    // ✅ Only run when status transitions to AUTHENTICATED (i.e. after useLogin
    // sets it). At this point the server has already set the auth cookie in the
    // login response, so authorize will succeed.
    if (authContextInfo.auth.status !== AUTH_STATUS.AUTHENTICATED) {
      return;
    }

    (async () => {
      try {
        sendAppLog(
          LOG_LEVEL.INFO,
          "Started authorize after login",
          buildLogContext(
            "auth",
            "authorize",
            // userId/companyId/storeId not yet available — authorize hasn't resolved yet
            undefined,
            undefined,
            undefined
          )
        );

        const { authorize } = await useAuthorizeMutation.mutateAsync();
        const { membership } = authorize;

        updateAuthContextInfo({
          ...authContextInfo,
          auth: {
            authorized: true,
            status: AUTH_STATUS.AUTHENTICATED
          },
          membership: membership ?? MembershipStatus.NONE
        });

        sendAppLog(
          LOG_LEVEL.INFO,
          "Authorize succeeded, redirecting to dashboard",
          buildLogContext(
            "auth",
            "authorize",
            // userId/companyId/storeId not populated on the authorize response object
            undefined,
            undefined,
            undefined,
            { membership: String(membership ?? MembershipStatus.NONE) }
          )
        );

        redirectFromLoginSuccess();
      } catch (e) {
        // ❌ Unexpected error during authorize — reset to unauthenticated
        captureAppException(
          "Authorize failed after login",
          e,
          buildLogContext(
            "auth",
            "authorize",
            // userId/companyId/storeId not available — authorize did not succeed
            undefined,
            undefined,
            undefined
          )
        );

        updateAuthContextInfo({
          ...authContextInfo,
          auth: { status: AUTH_STATUS.UNAUTHENTICATED }
        });

        if (e instanceof Error && e.message !== SUPER_ADMIN_NOT_ALLOWED_ERROR) {
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
        }
      }
    })();
    // ✅ Only re-run when auth status changes — not on every render
  }, [authContextInfo.auth.status]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: {
          xs: "column",
          sm: "column",
          md: "row"
        },
        width: "100%",
        minHeight: "100vh",
        height: "100vh",
        position: "relative"
      }}
    >
      {/* Left side */}
      <Box
        sx={{
          display: { xs: "none", sm: "none", md: "flex" },
          width: { md: "50%" },
          minHeight: { md: "100vh" },
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <AuthSidePanel />
      </Box>

      {/* Right side */}
      <AuthRightSidePanel>
        {(authContextInfo.auth.status === AUTH_STATUS.VERIFY_EMAIL_REQUIRED ||
          authContextInfo.auth.status ===
            AUTH_STATUS.EMAIL_VERIFICATION_AND_PASSWORD_CHANGE_REQUIRED) &&
          loginInfo && (
            <OtpVerification
              otpType="email"
              onBackToLogin={backToLogin}
              handleSubmitOtp={handleSubmitOtp}
            />
          )}
        {authContextInfo.auth.status === AUTH_STATUS.UNAUTHENTICATED && (
          <LoginForm setLoginInfo={setLoginInfo} />
        )}
        {authContextInfo.auth.status ===
          AUTH_STATUS.CHANGE_PASSWORD_REQUIRED && (
          <LoginChangePassword onChangeSuccess={backToLogin} />
        )}
      </AuthRightSidePanel>
      <SmallScreenFooter />
    </Box>
  );
};

export default Login;
